import {
  AppointmentEvent,
  AppointmentPackage,
  GetAppointmentDiscounts,
  GetAppointmentHistoryResult,
  GetAppointmentPackages,
  GetAppointmentTypesResult,
  PostAppointmentListViewResult,
  PostAppointmentMultipleCalendarViewResult,
  PostAppointmentSingleStaffCalendarResult,
} from "../../apis/appointmentApi";
import { getDayofTheWeekValue } from "../../utils/misc";
import * as actions from "../actions/apppointmentActions";

type ValueOf<T> = T[keyof T];

type Action = ReturnType<ValueOf<typeof actions>>;

interface AppointmentState {
  appointmentTypes: GetAppointmentTypesResult;
  appointmentTypesLoading: boolean;
  appointmentMultipleCalendarView: PostAppointmentMultipleCalendarViewResult;
  appointmentStaffCalendarView: PostAppointmentSingleStaffCalendarResult;
  appointmentListView: PostAppointmentListViewResult;
  appointmentDiscounts: GetAppointmentDiscounts;
  appointmentPackages: GetAppointmentPackages;
  appointmentPackageLandingPage?: AppointmentPackage;
  bookedAppointments: {
    data: any[];
  };
  appointmentHistory: GetAppointmentHistoryResult;
  appointmentWorkingHours: {
    workingHours: [];
  };
  appointmentIsLoadingCancel?: boolean;
}

const initialState: AppointmentState = {
  appointmentTypes: {
    data: [],
  },
  appointmentTypesLoading: false,
  appointmentMultipleCalendarView: {
    blockedData: [],
    eventData: [],
  },
  appointmentStaffCalendarView: {
    blockedData: [],
    eventData: [],
  },
  appointmentListView: {
    data: [],
  },
  appointmentDiscounts: {
    data: [],
  },
  appointmentPackages: {
    data: [],
  },
  bookedAppointments: {
    data: [],
  },
  appointmentHistory: {
    schedule: [],
  },
  appointmentWorkingHours: {
    workingHours: [],
  },
};

export default function reducer(
  state = initialState,
  action: Action,
): AppointmentState {
  switch (action.type) {
    case "SET_APPOINTMENT_TYPES":
      return {
        ...state,
        appointmentTypes: action.appointmentTypes,
      };

    case "SET_APPOINTMENT_TYPE":
      return {
        ...state,
        appointmentTypes: {
          ...state.appointmentTypes,
          data: [action.appointmentType.data, ...state.appointmentTypes.data],
        },
      };
    case "SET_LIST_VIEW":
      return {
        ...state,
        appointmentListView: action.listView,
      };
    case "SET_MULTIPLE_CALENDAR_VIEW":
      return {
        ...state,
        appointmentMultipleCalendarView: action.multipleCalendarView,
      };
    case "SET_SINGLE_STAFF_CALENDAR_VIEW":
      return {
        ...state,
        appointmentStaffCalendarView: action.staffCalendarView,
      };

    case "SET_APPOINTMENT_DISCOUNTS":
      return {
        ...state,
        appointmentDiscounts: action.appointmentDiscounts,
      };

    case "SET_APPOINTMENT_DISCOUNT":
      return {
        ...state,
        appointmentDiscounts: {
          ...state.appointmentDiscounts,
          data: [
            action.appointmentDiscount.data,
            ...state.appointmentDiscounts.data,
          ],
        },
      };

    case "SET_APPOINTMENT_PACKAGES":
      return {
        ...state,
        appointmentPackages: action.appointmentPackages,
      };

    case "SET_APPOINTMENT_PACKAGE":
      return {
        ...state,
        appointmentPackages: {
          ...state.appointmentPackages,
          data: [
            action.appointmentPackage.data,
            ...state.appointmentPackages.data,
          ],
        },
      };
    case "UPDATE_APPOINTMENT_PACKAGE":
      return {
        ...state,
        appointmentPackages: {
          ...state.appointmentPackages,
          data: state.appointmentPackages.data.map((apptpackage) => {
            if (apptpackage.uuid === action.appointmentPackage.data.uuid) {
              return action.appointmentPackage.data;
            }
            return apptpackage;
          }),
        },
      };

    case "UPDATE_APPOINTMENT_DISCOUNT":
      return {
        ...state,
        appointmentDiscounts: {
          ...state.appointmentDiscounts,
          data: state.appointmentDiscounts.data.map((discount) => {
            if (discount.uuid === action.appointmentDiscount.data.uuid) {
              return action.appointmentDiscount.data;
            }
            return discount;
          }),
        },
      };

    case "SET_BOOK_APPOINTMENT":
      return {
        ...state,
        bookedAppointments: {
          ...state.bookedAppointments,
          data: [action.appointment.data, ...state.bookedAppointments.data],
        },
      };

    case "SET_APPOINTMENT_HISTORY":
      return {
        ...state,
        appointmentHistory: action.appointmentHistory,
      };

    case "SET_SWITCH_STAFF":
      return {
        ...state,
        appointmentHistory: {
          ...state.appointmentHistory,
          schedule: state.appointmentHistory.schedule.map((appointment) => {
            if (appointment.uuid === action.staff.appointmentId) {
              return {
                ...appointment,
                staff: {
                  ...appointment.staff,
                  uuid: action.staff.user_uuid,
                  firstName: action.staff.first_name,
                  lastName: action.staff.last_name,
                },
              };
            }
            return appointment;
          }),
        },
      };

    case "UPDATE_APPOINTMENT_TYPE":
      return {
        ...state,
        appointmentTypes: {
          ...state.appointmentTypes,
          data: state.appointmentTypes.data.map((appointmentType) => {
            if (appointmentType.uuid === action.appointmentType.data.uuid) {
              return action.appointmentType.data;
            }
            return appointmentType;
          }),
        },
      };

    case "SET_APPOINTMENT_HISTORY_STATUS":
      return {
        ...state,
        appointmentHistory: {
          ...state.appointmentHistory,
          schedule: state.appointmentHistory.schedule.map((appointment) => {
            if (appointment.uuid === action.payload.appointmentId) {
              return {
                ...appointment,
                eventStatus: action.payload.status,
              };
            }
            return appointment;
          }),
        },
      };

    case "CANCEL_APPOINTMENT":
      return {
        ...state,
        appointmentListView: {
          ...state.appointmentListView,
          data:
            state.appointmentListView.data.map((appointment) => {
              if (action.payload.day !== appointment.date) return appointment;
              return {
                date: appointment.date,
                entries: appointment.entries.filter(
                  (e) => e.uuid !== action.payload.uuid,
                ),
              };
            }) || [],
        },
      };

    case "CANCEL_APPOINTMENT_MULTI_VIEW":
      return {
        ...state,
        appointmentMultipleCalendarView: {
          ...state.appointmentMultipleCalendarView,
          eventData: [
            ...state.appointmentMultipleCalendarView.eventData.filter(
              (data) => data.uuid !== action.payload,
            ),
          ],
        },
      };

    case "CANCEL_APPOINTMENT_SINGLE_STAFF":
      return {
        ...state,
        appointmentStaffCalendarView: {
          ...state.appointmentStaffCalendarView,
          eventData: [
            ...state.appointmentStaffCalendarView.eventData.filter(
              (data) => data.uuid !== action.payload,
            ),
          ],
        },
      };

    case "SET_APPOINTMENT_LIST_STATUS":
      return {
        ...state,
        appointmentListView: {
          ...state.appointmentListView,
          data:
            state.appointmentListView.data.map((appointment) => {
              if (action.payload.day !== appointment.date) return appointment;
              return {
                date: appointment.date,
                entries: appointment.entries.map((e) => {
                  if (action.payload.uuid !== e.uuid) return e;
                  return {
                    ...e,
                    status: action.payload.status,
                  };
                }),
              };
            }) || [],
        },
      };

    case "SET_SWITCH_STAFF_LIST_VIEW":
      return {
        ...state,
        appointmentListView: {
          ...state.appointmentListView,
          data: state.appointmentListView.data.map((appt) => {
            if (action.payload.day !== appt.date) return appt;
            return {
              ...appt,
              entries: appt.entries.map((e) => {
                if (action.payload.uuid !== e.uuid) return e;
                const { uuid, firstName, lastName } = action.payload;
                return {
                  ...e,
                  staff: {
                    ...e.staff,
                    uuid,
                    firstName,
                    lastName,
                  },
                };
              }),
            };
          }),
        },
      };

    case "SET_RESCHEDULE_APPOINTMENT":
      return {
        ...state,
        appointmentHistory: changeAppointmentHistory(state, action.payload),
        appointmentMultipleCalendarView: changeAppointmentMultipleCalendarView(
          state,
          action.payload,
        ),
        appointmentStaffCalendarView: changeAppointmentStaffCalendarView(
          state,
          action.payload,
        ),
        appointmentListView: changeAppointmentListView(state, action.payload),
      };

    case "REMOVE_APPOINTMENT_TYPE":
      return {
        ...state,
        appointmentTypes: {
          ...state.appointmentTypes,
          data:
            state.appointmentTypes.data.filter(
              (appointmentType) =>
                appointmentType.uuid !== action.appointmentTypeId,
            ) || [],
        },
      };

    case "CHANGE_CANCEL_LOADING":
      return {
        ...state,
        appointmentIsLoadingCancel: action.payload,
      };

    case "REMOVE_APPOINTMENT_DISCOUNT":
      return {
        ...state,
        appointmentDiscounts: {
          ...state.appointmentDiscounts,
          data: state.appointmentDiscounts.data.filter(
            (discount) => discount.uuid !== action.discountId,
          ),
        },
      };

    case "REMOVE_APPOINTMENT_PACKAGE":
      return {
        ...state,
        appointmentPackages: {
          ...state.appointmentPackages,
          data: state.appointmentPackages.data.filter(
            (appointmentPackage) =>
              appointmentPackage.uuid !== action.packageId,
          ),
        },
      };

    case "SET_APPOINTMENT_WORKING_HOURS":
      return {
        ...state,
        appointmentWorkingHours: {
          ...action.payload,
          workingHours: action.payload.workingHours.sort((a, b) => {
            return (
              getDayofTheWeekValue(a.dayOfTheWeek) -
              getDayofTheWeekValue(b.dayOfTheWeek)
            );
          }),
        },
      };

    case "SET_APPOINTMENT_PACKAGE_LANDING_PAGE":
      return {
        ...state,
        appointmentPackageLandingPage: action.appointmentPackage,
      };

    default:
      return state;
  }
}

interface ReschedulePayload {
  date: string;
  uuid: string;
  previousDay: string;
  entries: AppointmentEvent[];
}

function changeAppointmentHistory(
  state: AppointmentState,
  payload: ReschedulePayload,
) {
  const schedule = state.appointmentHistory.schedule;
  if (!schedule) {
    return state.appointmentHistory;
  }
  const entry = payload?.entries?.[0];
  const entryUpdate = {
    uuid: entry.uuid,
    startTime: entry.start,
    endTime: entry.end,
  };
  const nextSchedule = schedule.map((item) => {
    if (item.uuid === entryUpdate.uuid) {
      return {
        ...item,
        ...entryUpdate,
      };
    }
    return item;
  });
  return {
    ...state.appointmentHistory,
    schedule: nextSchedule,
  };
}

function changeAppointmentMultipleCalendarView(
  state: AppointmentState,
  payload: ReschedulePayload,
): PostAppointmentMultipleCalendarViewResult {
  const eventData = state.appointmentMultipleCalendarView?.eventData;
  if (!eventData) {
    return state.appointmentMultipleCalendarView;
  }
  const entry = payload?.entries?.[0];
  const entryUpdate = {
    uuid: entry.uuid,
    startTime: new Date(entry.start).getTime(),
    start: entry.start,
    end: entry.end,
  };
  const nextEventData = eventData.map((item) => {
    if (item.uuid === entryUpdate.uuid) {
      return {
        ...item,
        ...entryUpdate,
      };
    }
    return item;
  });
  return {
    ...state.appointmentMultipleCalendarView!,
    eventData: nextEventData,
  };
}

function changeAppointmentStaffCalendarView(
  state: AppointmentState,
  payload: ReschedulePayload,
): PostAppointmentSingleStaffCalendarResult {
  const eventData = state.appointmentStaffCalendarView?.eventData;
  const entry = payload?.entries?.[0];
  const entryUpdate = {
    uuid: entry.uuid,
    startTime: new Date(entry.start).getTime(),
    start: entry.start,
    end: entry.end,
    day: entry.day,
  };
  const nextEventData = eventData.map((item) => {
    if (item.uuid === entryUpdate.uuid) {
      return {
        ...item,
        ...entryUpdate,
      };
    }
    return item;
  });
  return {
    ...state.appointmentStaffCalendarView,
    eventData: nextEventData,
  };
}

function changeAppointmentListView(
  state: AppointmentState,
  payload: ReschedulePayload,
): PostAppointmentListViewResult {
  const data = state.appointmentListView.data;
  let newListView = data;

  // remove previous entry;
  newListView = newListView.map((appt) => {
    if (onlyDate(payload.previousDay) !== onlyDate(appt.date)) {
      return appt;
    }
    return {
      date: appt.date,
      entries: appt.entries.filter((item) => item.uuid !== payload.uuid),
    };
  });

  const hasDate = data.find(
    (appt) => onlyDate(appt.date) === onlyDate(payload.date),
  );
  if (!hasDate) {
    newListView = [...newListView, payload].sort(sortAppointmentListViewData);
  } else {
    newListView = newListView.map((appt) => {
      if (onlyDate(payload.date) !== onlyDate(appt.date)) {
        return appt;
      }
      return {
        date: appt.date,
        entries: [...appt.entries, ...payload.entries].sort(
          sortAppointmentEvent,
        ),
      };
    });
  }

  return {
    ...state.appointmentListView!,
    data: newListView,
  };
}

function onlyDate(dateString: string) {
  return dateString.split("T")[0];
}

function sortAppointmentListViewData(a: { date: string }, b: { date: string }) {
  return new Date(a.date).getTime() - new Date(b.date).getTime();
}

function sortAppointmentEvent(a: AppointmentEvent, b: AppointmentEvent) {
  return new Date(a.start).getTime() - new Date(b.start).getTime();
}
